import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';

const AdminLogin = () => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [sessionExpired, setSessionExpired] = useState(false);
    // MFA state: null | 'setup' (enrol TOTP) | 'code' (enter TOTP code)
    const [mfaStep, setMfaStep] = useState(null);
    const [mfaSession, setMfaSession] = useState('');
    const [secretCode, setSecretCode] = useState('');
    const [mfaCode, setMfaCode] = useState('');
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Check for expired session on mount
    useEffect(() => {
        if (searchParams.get('expired') === 'true') {
            setSessionExpired(true);
            // Clear the expired param from URL
            searchParams.delete('expired');
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, setSearchParams]);

    const storeTokensAndGo = (data) => {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminRefreshToken', data.refreshToken);
        localStorage.setItem('adminEmail', data.email || '');
        navigate('/admin/dashboard');
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/login`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.challengeName === 'MFA_SETUP') {
                    setMfaSession(data.session);
                    setSecretCode(data.secretCode);
                    setMfaStep('setup');
                } else if (data.challengeName === 'SOFTWARE_TOKEN_MFA') {
                    setMfaSession(data.session);
                    setMfaStep('code');
                } else {
                    storeTokensAndGo(data);
                }
            } else {
                setError('Invalid username or password');
            }
        } catch (err) {
            setError('Login failed');
        }
    };

    const handleMfaSubmit = async (e) => {
        e.preventDefault();
        setError('');
        const endpoint = mfaStep === 'setup' ? '/admin/mfa/verify-setup' : '/admin/mfa/respond';
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}${endpoint}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, session: mfaSession, code: mfaCode })
            });
            const data = await response.json();

            if (response.ok) {
                storeTokensAndGo(data);
            } else {
                setError(data.error || 'MFA verification failed');
                if ((data.error || '').includes('Session expired')) {
                    setMfaStep(null);
                    setMfaCode('');
                }
            }
        } catch (err) {
            setError('MFA verification failed');
        }
    };

    const otpauthUri = `otpauth://totp/${encodeURIComponent('Migration Experts Admin')}:${encodeURIComponent(username)}?secret=${secretCode}&issuer=${encodeURIComponent('The Migration Experts')}`;

    return (
        <div className="py-20 bg-gradient-to-b from-primary-50 to-white">
        <div className="flex items-center justify-center">
        <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-md relative">
        <h1 className="text-2xl font-light text-primary-900 mb-6">
        Admin <span className="font-normal">Login</span>
        </h1>
        {sessionExpired && (
            <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
            Your session has expired. Please log in again.
            </div>
        )}
        {!mfaStep ? (
            <form onSubmit={handleLogin}>
            <input
            type="text"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            placeholder="Username"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4"
            required
            />
            <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4"
            required
            />
            {error && <p className="text-red-600 mb-4">{error}</p>}
            <button type="submit" className="btn-primary w-full">
            Login
            </button>
            </form>
        ) : (
            <form onSubmit={handleMfaSubmit}>
            {mfaStep === 'setup' && (
                <>
                <div className="mb-4 p-3 bg-yellow-100 border border-yellow-400 text-yellow-800 rounded">
                Multi-factor authentication is required. Scan this QR code with your
                authenticator app, then enter the 6-digit code.
                </div>
                <div className="flex justify-center mb-4">
                <QRCodeSVG value={otpauthUri} size={180} />
                </div>
                <p className="text-xs text-gray-500 mb-4 break-all">
                Manual entry key: {secretCode}
                </p>
                </>
            )}
            {mfaStep === 'code' && (
                <p className="text-gray-600 mb-4">
                Enter the 6-digit code from your authenticator app.
                </p>
            )}
            <input
            type="text"
            inputMode="numeric"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            placeholder="Authenticator code"
            className="w-full px-4 py-3 border border-gray-300 rounded-lg mb-4"
            autoComplete="one-time-code"
            required
            />
            {error && <p className="text-red-600 mb-4">{error}</p>}
            <button type="submit" className="btn-primary w-full">
            Verify
            </button>
            <button
            type="button"
            onClick={() => {
                setMfaStep(null);
                setMfaCode('');
                setError('');
            }}
            className="btn-secondary w-full mt-2"
            >
            Back to Login
            </button>
            </form>
        )}

        <div className="mt-6 pt-6 border-t border-gray-200">
        <Link to="/" className="btn-secondary w-full text-center block">
        Return to Home
        </Link>
        </div>
        </div>
        </div>
        </div>
    );
};

export default AdminLogin;
